
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  standalone: true,
  imports: [MatIconModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true,
    },
  ],
})
export class ImageUploadComponent implements ControlValueAccessor {
  @Input() formControl: FormControl | null = null;
  @Input() multiple = true;
  @Input() maxSizeMB = 10;
  @Input() set initialUrls(urls: string[] | null) {
    if (this.isFirstLoad) {
      if (urls && urls.length) {
        this.previewImages = [...urls];
        this.allFiles = [...urls];
        this.currentSlide = 0;
      }
      this.isFirstLoad = false;
    }
  }

  private isFirstLoad = true;
  private onChange: any = () => {};
  private onTouched: any = () => {};
  protected isDragging = false;
  protected previewImages: string[] = [];
  protected allFiles: (string | File)[] = [];
  protected currentSlide = 0;
  protected fileUploadId = 'image-upload-input-' + Math.random().toString(36).substring(2, 10);

  writeValue(value: (string | File)[]): void {
    if (value) {
      this.allFiles = value;
      this.previewImages = value.filter((file) => typeof file === 'string');
      this.currentSlide = 0;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  markAsTouched(): void {
    this.onTouched();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    const files = Array.from(event.dataTransfer?.files || []);
    const validFiles = this.handleFiles(files);
    this.allFiles.push(...validFiles);

    if (this.formControl) {
      this.formControl.setValue(this.allFiles);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }
    const files = Array.from(input.files);
    const validFiles = this.handleFiles(files);
    this.allFiles.push(...validFiles);

    if (this.formControl) {
      this.formControl.setValue(this.allFiles);
    }
  }

  handleFiles(files: File[]) {
    const validFiles = files.filter((file) => file.size / 1024 / 1024 <= this.maxSizeMB);

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewImages.push(e.target.result);
        if (this.previewImages.length === validFiles.length) {
          this.currentSlide = 0;
        }
      };
      reader.readAsDataURL(file);
    });

    return validFiles;
  }

  removeImage(index: number) {
    this.previewImages.splice(index, 1);
    this.allFiles.splice(index, 1);

    if (this.previewImages.length === 0) {
      this.currentSlide = 0;
    } else if (this.currentSlide >= this.previewImages.length) {
      this.currentSlide = this.previewImages.length - 1;
    }
  }

  prevSlide() {
    if (this.currentSlide > 0) this.currentSlide--;
  }

  nextSlide() {
    if (this.currentSlide < this.previewImages.length - 1) this.currentSlide++;
  }
}
